import { Box, Flex } from '@chakra-ui/react';
import { DateInputComp } from 'components/Views/components/DateInputComp';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { addDays, addYears, format, parseISO, subYears } from 'date-fns';
import { useDispatch } from 'react-redux';
import { setReviewTypeAndDetails, useReviewCreationData, useReviewTypeAndDetails } from 'reducers/reviewCreationSlice';
import { getDateWithEndOfDay, getDateWithStartOfDay } from 'utils/helpers/dateHelpers';

import { BulletList } from 'assets/assetsV1/iconComponents';

import { modifyString, StringModifier } from 'constants/copyHelpers';

import { DatePicker } from 'uiToolkitV1/DatePicker';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import InputLabel from 'uiToolkitV1/InputLabel';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import {
  useGetReviewTypeAndDetails,
  useSaveReviewTypeAndDetails,
} from '../Hooks/dataHooks/reviewTypeAndDetailsDataHooks';
import { ReviewCreationFooter } from '../ReviewCreationFooter';

const TimeFrameSelection = () => {
  const { startDate, endDate } = useReviewTypeAndDetails();
  const dispatch = useDispatch();

  const startDateObj = startDate ? getDateWithStartOfDay(parseISO(startDate)) : null;
  const endDateObj = endDate ? getDateWithEndOfDay(parseISO(endDate)) : null;

  const minEndDate = startDate ? addDays(startDateObj, 1) : subYears(getDateWithEndOfDay(), 5);
  const maxStartDate = endDate ? addDays(endDateObj, -1) : addYears(getDateWithStartOfDay(), 5);
  const minStartDate = subYears(getDateWithStartOfDay(), 5);
  const maxEndDate = addYears(getDateWithEndOfDay(), 5);
  const { objectivesName } = useOrganisationSettings();

  const onChange = (date, isStartDate) => {
    const simpleDate = format(date, 'yyyy-MM-dd');
    if (isStartDate) {
      dispatch(setReviewTypeAndDetails({ startDate: simpleDate }));
    } else {
      dispatch(setReviewTypeAndDetails({ endDate: simpleDate }));
    }
  };

  return (
    <Box px={2} py="10px" pt={3} mb={3}>
      <Flex alignItems="center" mb={3}>
        <TextBodyMedium mr={2}>
          {' '}
          Select the date range within which you are assessing the employees&apos; performance:
        </TextBodyMedium>
        <TooltipInfoV1
          title={`If your review cycle includes ${modifyString(
            objectivesName,
            StringModifier.PLURAL_TITLE_CASE
          )}, they will be picked on the basis of this date range`}
        />
      </Flex>

      <Flex gap={8} w="600px">
        <InputLabel labelText="Start Date" containerProps={{ px: '0px', py: '0px', flexGrow: 1 }}>
          <DatePicker
            CustomInput={DateInputComp}
            selectedDate={startDateObj}
            onChange={(val) => onChange(val, true)}
            customInputProps={{ width: '100%' }}
            maxDate={maxStartDate}
            minDate={minStartDate}
            popperPlacement="right-start"
          />
        </InputLabel>

        <InputLabel labelText="End Date" containerProps={{ px: '0px', py: '0px', flexGrow: 1 }}>
          <DatePicker
            CustomInput={DateInputComp}
            selectedDate={endDateObj}
            onChange={(val) => onChange(val)}
            customInputProps={{ width: '100%' }}
            minDate={minEndDate}
            maxDate={maxEndDate}
            popperPlacement="right-start"
          />
        </InputLabel>
      </Flex>
    </Box>
  );
};

const Name = () => {
  const dispatch = useDispatch();
  const { name = '' } = useReviewTypeAndDetails();

  const onChange = (e) => {
    dispatch(setReviewTypeAndDetails({ name: e.target.value }));
  };

  return (
    <Box px={2} py="10px" pt={3} mb={3}>
      <TextBodyMedium mb={1}>Review Cycle Name</TextBodyMedium>
      <InputV2 placeholder="Enter a cycle name" isDashed w="600px" onChange={onChange} value={name} maxLength={255} />
    </Box>
  );
};

const Footer = () => {
  const { name, startDate, endDate } = useReviewTypeAndDetails();
  const saveDataMutation = useSaveReviewTypeAndDetails();

  const isContinueDisabled = !name || !startDate || !endDate || saveDataMutation.isLoading;

  const handleContinue = () => {
    saveDataMutation.mutate();
    console.log('continue');
  };

  return <ReviewCreationFooter isContinueDisabled={isContinueDisabled} onContinue={handleContinue} />;
};

const ReviewTypeAndDetails = () => {
  const { reviewCycleId } = useReviewCreationData();

  const { isLoading } = useGetReviewTypeAndDetails({ reviewCycleId });

  return (
    <>
      <ExpandablePaper showLoader={reviewCycleId && isLoading}>
        <ExpandablePaperHeader
          title="Timeline & Name"
          leftIconComponent={<BulletList boxSize="24px" color="icon.g2" />}
        />
        <ExpandablePaperBody>
          <TimeFrameSelection />
          <Name />
        </ExpandablePaperBody>
      </ExpandablePaper>

      <Footer />
    </>
  );
};

export { ReviewTypeAndDetails };
